import React, { useState } from 'react';
import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser';

interface PasskeySetupPageProps {
  userName?: string | null;
  onComplete: () => void;
  onSkip: () => void;
  onNeverRemind: () => void;
}

/**
 * Full-screen, plain-language passkey enrollment for non-technical staff (e.g. sales).
 */
const PasskeySetupPage: React.FC<PasskeySetupPageProps> = ({ userName, onComplete, onSkip, onNeverRemind }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const supported = typeof window !== 'undefined' && browserSupportsWebAuthn();

  const handleNeverRemind = async () => {
    setError('');
    setDismissing(true);
    try {
      const res = await fetch('/api/auth/passkeys/dismiss-reminders', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not save your choice. Please try again.');
        return;
      }
      onNeverRemind();
    } catch {
      setError('Could not save your choice. Please try again.');
    } finally {
      setDismissing(false);
    }
  };

  const handleSetup = async () => {
    setError('');
    if (!supported) {
      setError('This device or browser cannot use quick login. Please try Chrome or Safari, or use a phone.');
      return;
    }

    setBusy(true);
    try {
      const optionsRes = await fetch('/api/auth/passkeys/register/options', {
        method: 'POST',
        credentials: 'include',
      });
      const optionsData = await optionsRes.json();
      if (!optionsRes.ok) {
        setError(optionsData.error || 'Could not start setup. Please try again.');
        return;
      }

      const attestation = await startRegistration({ optionsJSON: optionsData });

      const verifyRes = await fetch('/api/auth/passkeys/register/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          response: attestation,
          friendlyName: 'My device',
        }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) {
        setError(verifyData.error || 'Setup failed. Please try again.');
        return;
      }

      setSuccess(true);
      setTimeout(() => onComplete(), 1200);
    } catch (err: unknown) {
      const name = err && typeof err === 'object' && 'name' in err ? String((err as { name: string }).name) : '';
      if (name === 'NotAllowedError') {
        setError('Setup was cancelled. Tap the button again when you are ready.');
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 px-4 py-10">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-25" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-25" />
      </div>

      <div className="relative z-10 w-full max-w-lg">
        <div className="backdrop-blur-3xl bg-white/80 border border-white/60 rounded-3xl shadow-2xl p-8 sm:p-10">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="bg-white p-3 rounded-2xl shadow-md border border-slate-100 mb-5">
              <img
                src="https://webfiles.remapp.ae/web_remapp_ae/hjas-1-1.png"
                alt="REM"
                className="h-14 w-auto"
              />
            </div>
            <p className="text-sm font-semibold text-indigo-600 mb-2">
              {userName ? `Hi ${userName.split(' ')[0]}` : 'Almost done'}
            </p>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 leading-tight">
              Set up quick login
            </h1>
            <p className="text-slate-600 text-base leading-relaxed max-w-md">
              This lets you open REM with your <strong>fingerprint</strong> or <strong>face</strong> next time —
              no need to type a password.
            </p>
          </div>

          {success ? (
            <div className="text-center py-6 space-y-3">
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-lg font-semibold text-emerald-800">All set!</p>
              <p className="text-sm text-slate-600">Opening your account…</p>
            </div>
          ) : (
            <>
              <ol className="space-y-4 mb-8 text-left">
                <li className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center text-sm">1</span>
                  <div>
                    <p className="font-semibold text-slate-900">Tap the big button below</p>
                    <p className="text-sm text-slate-600 mt-0.5">It starts the setup on this device.</p>
                  </div>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center text-sm">2</span>
                  <div>
                    <p className="font-semibold text-slate-900">Follow what your phone or computer shows</p>
                    <p className="text-sm text-slate-600 mt-0.5">Use Face ID, fingerprint, or Windows Hello when asked.</p>
                  </div>
                </li>
                <li className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center text-sm">3</span>
                  <div>
                    <p className="font-semibold text-slate-900">You are done</p>
                    <p className="text-sm text-slate-600 mt-0.5">Next time, just tap “Sign in with passkey”.</p>
                  </div>
                </li>
              </ol>

              {!supported && (
                <div className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                  This browser cannot use quick login. Please open this site in <strong>Chrome</strong> or <strong>Safari</strong>,
                  then try again. You can continue with your password for now.
                </div>
              )}

              {error && (
                <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm font-medium">
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={handleSetup}
                disabled={busy || dismissing || !supported}
                className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-lg font-semibold shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              >
                {busy ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Waiting for your device…
                  </span>
                ) : (
                  'Set up quick login now'
                )}
              </button>

              <button
                type="button"
                onClick={onSkip}
                disabled={busy || dismissing}
                className="w-full mt-4 py-3 text-slate-600 hover:text-slate-900 text-sm font-medium disabled:opacity-50"
              >
                Remind me next time I sign in
              </button>

              <button
                type="button"
                onClick={handleNeverRemind}
                disabled={busy || dismissing}
                className="w-full py-2 text-slate-400 hover:text-slate-600 text-xs font-medium disabled:opacity-50"
              >
                {dismissing ? 'Saving…' : "Don't remind me again"}
              </button>

              <p className="mt-6 text-center text-xs text-slate-500 leading-relaxed">
                Need help? Ask your manager or IT team — this only takes about 30 seconds.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasskeySetupPage;
